import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { InstrumentDto } from '@apna/shared'
import { cachedChain, cachedExpiries, catalogueRevision, prefetchChain, prefetchExpiries, subscribeCatalogue } from '@/feed/prefetchChain'

export interface ChainLadderRow {
  strike: number
  ce: InstrumentDto | null
  pe: InstrumentDto | null
}

export function groupIntoLadder(items: readonly InstrumentDto[]): ChainLadderRow[] {
  const byStrike = new Map<number, ChainLadderRow>()
  for (const instrument of items) {
    if (instrument.strike == null) continue
    const row = byStrike.get(instrument.strike) ?? { strike: instrument.strike, ce: null, pe: null }
    if (instrument.optionType === 'CE') row.ce = instrument
    else if (instrument.optionType === 'PE') row.pe = instrument
    byStrike.set(instrument.strike, row)
  }
  return [...byStrike.values()].sort((a, b) => a.strike - b.strike)
}

const NO_EXPIRIES: string[] = []
const NO_ROWS: ChainLadderRow[] = []

/** Read cached catalogue data DURING the switch render, never clear a ready ladder. */
export function useChainCatalogue(underlying: string) {
  useSyncExternalStore(subscribeCatalogue, catalogueRevision)
  const [selection, setSelection] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null)
  const expiries = cachedExpiries(underlying)?.expiries ?? NO_EXPIRIES
  const expiry = expiries.includes(selection[underlying] ?? '') ? selection[underlying]! : expiries[0] ?? null
  const key = `${underlying}|${expiry ?? ''}`
  const chain = expiry === null ? undefined : cachedChain(underlying, expiry)
  const rows = useMemo(() => chain ? groupIntoLadder(chain.items) : NO_ROWS, [chain])
  useEffect(() => {
    /*
     * NOTHING IS FETCHED FOR NO UNDERLYING — ROADMAP 9.66.
     *
     * The basket builder mounts its ladder before the trader has chosen what
     * to build on, so this is called with an empty string. Without the guard
     * that becomes a request for the expiries of "", which the catalogue
     * cannot answer — and the failure it records would be shown as an error
     * about a chain nobody asked for.
     */
    if (underlying === '') return
    let cancelled = false
    void prefetchExpiries(underlying)
      .then((result) => {
        const chosen = expiry ?? result.expiries[0]
        return chosen ? prefetchChain(underlying, chosen) : undefined
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure({ key, message: error instanceof Error ? error.message : 'Could not load the option chain.' })
      })
    return () => { cancelled = true }
  }, [underlying, expiry, key])
  const setExpiry = useCallback((value: string) => {
    setSelection((current) => ({ ...current, [underlying]: value }))
  }, [underlying])
  return {
    rows, expiries, expiry, setExpiry,
    loading: !chain && !(cachedExpiries(underlying) && expiries.length === 0),
    error: !chain && failure?.key === key ? failure.message : cachedExpiries(underlying) && expiries.length === 0 ? 'No contracts in the catalogue for this underlying.' : null,
  }
}
